/**
 * Generates a complete HTML page with alerts, fully self-contained with all styling
 * @returns The HTML content for alerts
 */
export const generateAlertsHTML = (): string => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Competition Alerts</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    }

    body {
      background-color: #121212;
      padding: 20px;
      color: #e0e0e0;
    }

    .alerts-container {
      max-width: 900px;
      margin: 0 auto;
      background-color: #1e1e1e;
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      overflow: hidden;
    }

    .alerts-header {
      background-color: #2d2d2d;
      color: white;
      padding: 20px 24px;
      font-size: 20px;
      font-weight: 700;
      display: flex;
      justify-content: space-between;
      align-items: center;
      letter-spacing: -0.02em;
    }

    .alerts-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
      gap: 16px;
      padding: 16px;
    }

    .alert-item {
      background-color: #252525;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
      padding: 18px;
      position: relative;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      border: 1px solid #333;
      overflow: hidden;
    }

    .alert-item:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 18px rgba(0, 0, 0, 0.15);
    }

    .alert-item.critical {
      border-top: 4px solid #ff3a5e;
    }

    .alert-item.high {
      border-top: 4px solid #ff9500;
    }

    .alert-item.medium {
      border-top: 4px solid #007aff;
    }

    .alert-item.low {
      border-top: 4px solid #34c759;
    }

    .alert-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 12px;
      align-items: center;
      position: relative;
      padding-bottom: 10px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .alert-competitor {
      font-weight: 700;
      color: #ffffff;
      font-size: 16px;
      max-width: 60%;
    }

    .alert-importance {
      font-size: 11px;
      text-transform: uppercase;
      font-weight: 600;
      padding: 3px 8px;
      border-radius: 6px;
      letter-spacing: 0.5px;
    }

    .alert-importance.critical {
      background-color: rgba(255, 58, 94, 0.15);
      color: #ff5a78;
    }

    .alert-importance.high {
      background-color: rgba(255, 149, 0, 0.15);
      color: #ffae3c;
    }

    .alert-importance.medium {
      background-color: rgba(0, 122, 255, 0.15);
      color: #3395ff;
    }

    .alert-importance.low {
      background-color: rgba(52, 199, 89, 0.15);
      color: #4ddb74;
    }

    .alert-time {
      color: #a0a0a0;
      font-size: 13px;
      font-weight: 500;
      margin-top: 8px;
    }

    .alert-content {
      margin-top: 12px;
    }

    .alert-metric {
      margin-bottom: 14px;
      font-size: 15px;
      color: #d0d0d0;
      line-height: 1.4;
    }

    .alert-values {
      display: flex;
      font-size: 14px;
      color: #b0b0b0;
      align-items: center;
      margin-top: 8px;
    }

    .alert-change {
      margin-left: 12px;
      padding: 4px 10px;
      border-radius: 20px;
      font-weight: 600;
      font-size: 12px;
    }

    .alert-change.increase {
      background-color: rgba(52, 199, 89, 0.15);
      color: #4ddb74;
    }

    .alert-change.decrease {
      background-color: rgba(255, 58, 94, 0.15);
      color: #ff5a78;
    }

    @media (max-width: 880px) {
      .alerts-grid {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 600px) {
      body {
        padding: 0;
      }

      .alerts-container {
        border-radius: 0;
        box-shadow: none;
      }

      .alert-item {
        border-radius: 8px;
      }
    }
  </style>
</head>
<body>
  <div class="alerts-container">
    <div class="alerts-header">
      Competition Alerts
    </div>

    <div class="alerts-grid">
      <div class="alert-item critical">
        <div class="alert-header">
          <div class="alert-competitor">Competitor A</div>
          <div class="alert-importance critical">Critical</div>
        </div>
        <div class="alert-time">30 minutes ago</div>
        <div class="alert-content">
          <div class="alert-metric">Price reduction on flagship product</div>
          <div class="alert-values">
            <span>$899 (was $999)</span>
            <span class="alert-change decrease">-10%</span>
          </div>
        </div>
      </div>

      <div class="alert-item high">
        <div class="alert-header">
          <div class="alert-competitor">Competitor B</div>
          <div class="alert-importance high">High</div>
        </div>
        <div class="alert-time">2 hours ago</div>
        <div class="alert-content">
          <div class="alert-metric">New feature launch</div>
          <div class="alert-values">
            <span>AI Assistant (was N/A)</span>
            <span class="alert-change increase">New</span>
          </div>
        </div>
      </div>

      <div class="alert-item medium">
        <div class="alert-header">
          <div class="alert-competitor">Competitor C</div>
          <div class="alert-importance medium">Medium</div>
        </div>
        <div class="alert-time">1 day ago</div>
        <div class="alert-content">
          <div class="alert-metric">Market share change</div>
          <div class="alert-values">
            <span>18.5% (was 15.2%)</span>
            <span class="alert-change increase">+3.3%</span>
          </div>
        </div>
      </div>

      <div class="alert-item low">
        <div class="alert-header">
          <div class="alert-competitor">Competitor D</div>
          <div class="alert-importance low">Low</div>
        </div>
        <div class="alert-time">3 days ago</div>
        <div class="alert-content">
          <div class="alert-metric">Social media engagement rate</div>
          <div class="alert-values">
            <span>4.2% (was 3.8%)</span>
            <span class="alert-change increase">+0.4%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
};

/**
 * Generates a complete HTML page with multiple alerts, fully self-contained with all styling
 * @returns The HTML content for a complete alerts page
 */
export const generateAlertsListHTML = (): string => {
  return generateAlertsHTML();
};
